import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  const handleCategoryClick = (catSlug: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类' },
              ]}
            />

            <div className="py-8 md:py-12">
              <div className="flex items-center gap-4 mb-4">
                {category && (
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                    <i className={`${category.icon} text-2xl`}></i>
                  </div>
                )}
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {category?.name || '未找到分类'}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {category?.description || ''}
                  </p>
                </div>
              </div>
            </div>

            <div className="pb-6 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-500 mr-2 whitespace-nowrap">切换分类：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            {articles.length > 0 ? (
              <>
                <div className="mb-6 text-sm text-foreground-600">
                  共找到 <strong className="text-foreground-950">{articles.length}</strong> 篇相关内容
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-inbox-line text-3xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                  暂无内容
                </h3>
                <p className="text-sm text-foreground-600 mb-6">
                  该分类下暂无文章，请浏览其他分类
                </p>
                <Link
                  to="/"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.guochanyiqu.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category?.name || '分类',
              },
            ],
          }),
        }}
      />
    </>
  );
}